import React, { useState } from "react";
import { X, MessageSquare, Star, Smile, Frown, Meh, Send } from "lucide-react";
import { CONFIG } from "../../config";
import { toast } from "react-hot-toast";
import api from "../../api/axios";

export default function FeedbackModal({ isOpen, onClose, provider, onSubmit }) {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Reset success state when reopening
    React.useEffect(() => {
        if (isOpen) setIsSuccess(false);
    }, [isOpen]);

    if (!isOpen) return null;

    // Render Success State
    if (isSuccess) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
                <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-200">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <X size={20} />
                    </button>

                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Star className="fill-green-500 text-green-500 w-8 h-8" />
                    </div>

                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Thank you!</h2>
                    <p className="text-gray-600 dark:text-gray-300">
                        For sharing your feedback. It helps us grow.
                    </p>
                </div>
            </div>
        );
    }

    const handleSubmit = async () => {
        if (!provider?.id) return;
        setIsSubmitting(true);
        // const toastId = toast.loading("Submitting feedback...");

        try {
            await api.post(`/feedback`, {
                targetUserId: provider.id, // Sending USER ID
                rating,
                comment
            });

            // toast.success("Thank you for your feedback!", { id: toastId });
            setIsSuccess(true);

            // Optional: Reset form
            setRating(5);
            setComment("");
        } catch (err) {
            toast.error("Could not save feedback");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Card */}
            <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 animate-in zoom-in-95 duration-200">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <X size={20} />
                </button>

                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Rate your session
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                    How was your experience with <span className="font-semibold text-brand-purple">{provider?.name || "the provider"}</span>?
                    Your feedback stays anonymous to the public.
                </p>

                {/* Stars */}
                <div className="flex justify-center gap-2 mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => setRating(star)}
                            className="p-1 transition-transform hover:scale-110 active:scale-95 focus:outline-none"
                        >
                            <Star
                                size={32}
                                className={star <= rating
                                    ? "fill-yellow-400 text-yellow-400 drop-shadow-sm"
                                    : "text-gray-300 dark:text-gray-600"
                                }
                            />
                        </button>
                    ))}
                </div>

                {/* Comment */}
                <div className="mb-6 relative">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Comment (Optional)
                    </label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value.slice(0, 50))} // Max 50
                        name="feedback-comment"
                        id="feedback-comment"
                        placeholder="Helpful? Good provider?"
                        rows={3}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-purple/50 focus:border-brand-purple outline-none transition-all resize-none text-sm"
                    />
                    <div className="absolute right-3 bottom-3 text-[10px] text-gray-400">
                        {comment.length}/50
                    </div>
                </div>

                {/* Action */}
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full py-3 bg-brand-gradient text-white rounded-xl font-medium shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                </button>

            </div>
        </div>
    );
}
